import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn, uuid } from '@/lib/utils';

type ToastTone = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  leaving?: boolean;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id' | 'leaving'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const EXIT_MS = 200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), EXIT_MS);
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id' | 'leaving'>) => {
      const id = uuid();
      setToasts((current) => [...current, { ...toast, id }]);
      // Errors stay long enough to be read and copied; confirmations do not need to.
      const ttl = toast.tone === 'error' ? 9_000 : 4_000;
      setTimeout(() => dismiss(id), ttl);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, description) => push({ tone: 'success', title, description }),
      error: (title, description) => push({ tone: 'error', title, description }),
      warning: (title, description) => push({ tone: 'warning', title, description }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col gap-2 no-print sm:start-auto sm:end-4 sm:w-full sm:max-w-sm">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONES: Record<ToastTone, { icon: typeof Info; iconClass: string; barClass: string }> = {
  success: { icon: CheckCircle2, iconClass: 'text-success', barClass: 'bg-success' },
  error: { icon: XCircle, iconClass: 'text-destructive', barClass: 'bg-destructive' },
  warning: { icon: AlertTriangle, iconClass: 'text-warning', barClass: 'bg-warning' },
  info: { icon: Info, iconClass: 'text-primary', barClass: 'bg-primary' },
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { icon: Icon, iconClass, barClass } = TONES[toast.tone];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-auto relative flex items-start gap-3 overflow-hidden rounded-lg border bg-card p-3 ps-4 text-card-foreground shadow-lg',
        toast.leaving
          ? 'animate-out fade-out-0 slide-out-to-right-8 duration-200 fill-mode-forwards rtl:slide-out-to-left-8'
          : 'animate-in fade-in-0 slide-in-from-bottom-3 duration-200',
      )}
    >
      <span className={cn('absolute inset-y-0 start-0 w-1', barClass)} aria-hidden />
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{toast.title}</p>
        {toast.description && <p className="mt-0.5 break-words text-xs text-muted-foreground">{toast.description}</p>}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-ring"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
